import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, Link } from 'react-router-dom'
import { api } from '../lib/api'
import { useAuthStore } from '../hooks/useAuth'

type Tab = 'portfolio' | 'orders' | 'roboto'

function parseNum(v: any, dec = 2) {
  const n = parseFloat(v ?? 0)
  return isNaN(n) ? 0 : parseFloat(n.toFixed(dec))
}
function fmt$(v: any, dec = 2) {
  const n = parseNum(v, dec)
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec })}`
}
function fmtPct(v: any) { const n = parseFloat(v ?? 0) * 100; return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%` }
function clr(v: any) { return parseFloat(v ?? 0) >= 0 ? 'var(--cash)' : 'var(--red)' }

//  Bracket Order Modal 
function OrderModal({ prefill, onClose }: { prefill?: any; onClose: () => void }) {
  const qc = useQueryClient()
  const [symbol,    setSymbol]    = useState(prefill?.ticker ?? '')
  const [side,      setSide]      = useState<'buy'|'sell'>(prefill?.direction === 'SELL' || prefill?.direction === 'SHORT' ? 'sell' : 'buy')
  const [qty,       setQty]       = useState(prefill?.qty ? String(prefill.qty) : '')
  const [entry,     setEntry]     = useState(prefill?.entry ?? '')
  const [stop,      setStop]      = useState(prefill?.stop ?? '')
  const [target,    setTarget]    = useState(prefill?.target1 ?? '')
  const [orderType, setOrderType] = useState<'market'|'limit'>(prefill?.qty ? 'market' : 'limit')
  const [error,     setError]     = useState('')
  const [success,   setSuccess]   = useState('')

  const mutation = useMutation({
    mutationFn: (order: any) => api.trading.placeOrder(order, 'paper'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orders'] })
      qc.invalidateQueries({ queryKey: ['positions'] })
      setSuccess('Order placed! Check your Alpaca paper account.')
    },
    onError: (e: any) => setError(e.message),
  })

  function submit() {
    setError('')
    if (!symbol || !qty) { setError('Symbol and quantity required'); return }
    const order: any = {
      symbol:        symbol.toUpperCase(),
      qty:           parseFloat(qty),
      side,
      type:          orderType,
      time_in_force: 'gtc',
      analysis_id:   prefill?.analysisId,
    }
    if (orderType === 'limit' && entry) order.limit_price = parseFloat(entry)
    if (stop)   order.stop_loss_price   = parseFloat(stop)
    if (target) order.take_profit_price = parseFloat(target)
    mutation.mutate(order)
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, padding: 16 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 6, width: '100%', maxWidth: 440, overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--edge)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.9rem', color: 'var(--bill)' }}>
            {prefill ? `Paper Trade · ${prefill.ticker}` : 'Place Paper Order'}
          </span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: '1.2rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: '18px', display: 'flex', flexDirection: 'column', gap: 13 }}>

          {prefill && (
            <div style={{ background: 'rgba(133,196,124,.06)', border: '1px solid var(--cash-dim)', borderRadius: 3, padding: '9px 12px', fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)', lineHeight: 1.7 }}>
              <span style={{ color: 'var(--cash)', fontWeight: 700 }}>From AI analysis:</span> Grade {prefill.grade} · {prefill.direction} · {prefill.confidence}% confidence
            </div>
          )}

          {/* Side */}
          <div style={{ display: 'flex', gap: 6 }}>
            {(['buy','sell'] as const).map(s => (
              <button key={s} onClick={() => setSide(s)} style={{
                flex: 1, padding: '8px', border: `1px solid ${side === s ? (s === 'buy' ? 'var(--cash)' : 'var(--red)') : 'var(--edge)'}`,
                background: side === s ? (s === 'buy' ? 'rgba(133,196,124,.1)' : 'rgba(224,92,92,.1)') : 'transparent',
                color: side === s ? (s === 'buy' ? 'var(--cash)' : 'var(--red)') : 'var(--muted)',
                fontFamily: 'Syne', fontWeight: 800, fontSize: '.78rem', borderRadius: 2, cursor: 'pointer', textTransform: 'uppercase',
              }}>{s}</button>
            ))}
          </div>

          {/* Symbol + Qty */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <div className="eyebrow" style={{ marginBottom: 5 }}>Symbol</div>
              <input value={symbol} onChange={e => setSymbol(e.target.value.toUpperCase())} placeholder="AAPL" maxLength={10}
                style={{ width: '100%', background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '8px 10px', color: 'var(--text)', fontFamily: 'Syne Mono', fontSize: '.78rem', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div>
              <div className="eyebrow" style={{ marginBottom: 5 }}>Shares</div>
              <input value={qty} onChange={e => setQty(e.target.value)} placeholder="10" type="number" min="1"
                style={{ width: '100%', background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '8px 10px', color: 'var(--text)', fontFamily: 'Syne Mono', fontSize: '.78rem', outline: 'none', boxSizing: 'border-box' }} />
            </div>
          </div>

          {/* Order type */}
          <div style={{ display: 'flex', gap: 6 }}>
            {(['market','limit'] as const).map(t => (
              <button key={t} onClick={() => setOrderType(t)} style={{
                flex: 1, padding: '6px', border: `1px solid ${orderType === t ? 'var(--cash-dim)' : 'var(--edge)'}`,
                background: orderType === t ? 'var(--stamp)' : 'transparent',
                color: orderType === t ? 'var(--cash)' : 'var(--muted)',
                fontFamily: 'Syne Mono', fontSize: '.65rem', borderRadius: 2, cursor: 'pointer', textTransform: 'uppercase',
              }}>{t}</button>
            ))}
          </div>

          {/* Price levels  from AI if prefilled */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            {/* Only the Entry/Limit row is conditional; Stop/Take-Profit are always shown. Previously the
                filter kept a second "Stop Loss" box on market orders that was bound to unused `entry`
                state → users typed a stop that never became stop_loss_price. Code review P1. */}
            {[
              { label: 'Entry / Limit', val: entry, set: setEntry, placeholder: '187.50', show: orderType === 'limit' },
              { label: 'Stop Loss',   val: stop,   set: setStop,   placeholder: '184.80', show: true },
              { label: 'Take Profit', val: target,  set: setTarget,  placeholder: '193.40', show: true },
            ].filter(f => f.show).map(f => (
              <div key={f.label}>
                <div className="eyebrow" style={{ marginBottom: 5 }}>{f.label}</div>
                <input value={f.val} onChange={e => f.set(e.target.value)} placeholder={f.placeholder} type="number" step="0.01"
                  style={{ width: '100%', background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '8px 8px', color: 'var(--text)', fontFamily: 'Syne Mono', fontSize: '.72rem', outline: 'none', boxSizing: 'border-box' }} />
              </div>
            ))}
          </div>

          <div style={{ background: 'rgba(212,168,67,.06)', border: '1px solid rgba(212,168,67,.2)', borderRadius: 2, padding: '8px 10px', fontFamily: 'Syne Mono', fontSize: '.56rem', color: 'var(--amber)', lineHeight: 1.6 }}>
            Paper trading only  no real money. Orders execute against live market data in your Alpaca paper account.
          </div>

          {error   && <div style={{ fontFamily: 'Syne Mono', fontSize: '.62rem', color: 'var(--red)' }}>x {error}</div>}
          {success && <div style={{ fontFamily: 'Syne Mono', fontSize: '.62rem', color: 'var(--cash)' }}>OK {success}</div>}

          <button onClick={submit} disabled={mutation.isPending} style={{
            padding: '11px', background: side === 'buy' ? 'var(--cash)' : 'var(--red)',
            color: 'var(--ink)', border: 'none', borderRadius: 2,
            fontFamily: 'Syne', fontWeight: 800, fontSize: '.82rem', cursor: 'pointer',
            opacity: mutation.isPending ? .5 : 1,
          }}>
            {mutation.isPending ? 'Placing...' : `Place ${side.toUpperCase()} Order ->`}
          </button>
        </div>
      </div>
    </div>
  )
}

//  Main Trading Page 
export default function Trading() {
  const { user } = useAuthStore()
  const [tab,         setTab]         = useState<Tab>('portfolio')
  const [orderModal,  setOrderModal]  = useState<any>(null)
  const [orderTab,    setOrderTab]    = useState<'open'|'history'>('open')
  const qc = useQueryClient()

  const { data: accountData, isError: accountError } = useQuery({
    queryKey: ['account'],
    queryFn: () => api.trading.account('paper'),
    retry: false,
    refetchInterval: 30_000,
  })

  // Sync D1 trade statuses from Alpaca on mount
  useEffect(() => {
    api.trading.syncTrades('paper')
      .then(() => qc.invalidateQueries({ queryKey: ['my-trades'] }))
      .catch(() => {})
  }, [])
  const { data: posData, isError: posError } = useQuery({
    queryKey: ['positions'],
    queryFn: () => api.trading.positions('paper'),
    retry: false,
    refetchInterval: 30_000,
  })
  const { data: ordersData } = useQuery({
    queryKey: ['orders'],
    queryFn: () => api.trading.orders('paper', 'all'),
    retry: false,
    staleTime: 0,
    refetchInterval: 15_000,
  })
  const { data: myTrades } = useQuery({
    queryKey: ['my-trades'],
    queryFn: () => api.trading.myTrades(),
    retry: false,
    staleTime: 0,
    refetchInterval: 15_000,
  })

  const [cancelError,    setCancelError]    = useState('')
  const [cancellingId,   setCancellingId]   = useState<string | null>(null)
  const [expandedPos,    setExpandedPos]    = useState<string | null>(null)
  const cancelMutation = useMutation({
    mutationFn: (id: string) => { setCancellingId(id); return api.trading.cancelOrder(id, 'paper') },
    onSuccess: () => {
      setCancelError('')
      setCancellingId(null)
      qc.invalidateQueries({ queryKey: ['orders'] })
      qc.invalidateQueries({ queryKey: ['my-trades'] })
    },
    onError: (e: any) => { setCancellingId(null); setCancelError(e.message ?? 'Cancel failed') },
  })

  const account   = accountData?.account
  const positions = Array.isArray(posData?.positions) ? posData.positions : []
  const orders    = Array.isArray(ordersData?.orders) ? ordersData.orders : []
  const noAlpaca  = accountError || posError

  // Day P&L = equity minus last session's equity (captures realized + unrealized)
  const equity     = parseNum(account?.equity)
  const lastEquity = parseNum(account?.last_equity)
  const dayPL      = lastEquity > 0 ? equity - lastEquity : parseNum(account?.unrealized_intraday_pl)
  const dayPLPct   = lastEquity > 0 ? (dayPL / lastEquity) * 100 : parseFloat(account?.unrealized_intraday_plpc ?? 0) * 100

  // Total P&L = equity minus starting capital ($100k for Alpaca paper)
  const PAPER_STARTING_CAPITAL = 100_000
  const totalPL    = equity > 0 ? equity - PAPER_STARTING_CAPITAL : 0
  const totalPLPct = equity > 0 ? (totalPL / PAPER_STARTING_CAPITAL) * 100 : 0

  //  Mr. Roboto algo state 
  const { data: roboSettings, refetch: refetchRoboSettings } = useQuery({
    queryKey: ['robo-settings'],
    queryFn:  () => api.robotrader.getSettings(),
    retry: false,
    staleTime: 10_000,
  })
  const { data: roboStatusData } = useQuery({
    queryKey: ['robo-status'],
    queryFn:  () => api.robotrader.status(),
    retry: false,
    refetchInterval: 15_000,
  })
  const [roboSaveStatus, setRoboSaveStatus] = useState<string | null>(null)
  const roboSaveMutation = useMutation({
    mutationFn: (data: any) => api.robotrader.saveSettings(data),
    onSuccess: (res) => {
      refetchRoboSettings()
      const msg = res?.closeResult?.message
        ? `OK Activated  ${res.closeResult.message}`
        : 'OK Mr. Roboto activated  scanning starts at next market open'
      setRoboSaveStatus(msg)
      setTimeout(() => setRoboSaveStatus(null), 8000)
    },
    onError: (e: any) => {
      setRoboSaveStatus(`x Error: ${e.message}`)
      setTimeout(() => setRoboSaveStatus(null), 8000)
    },
  })
  const roboDeactivateMutation = useMutation({
    mutationFn: () => api.robotrader.deactivate(),
    onSuccess: (res) => {
      refetchRoboSettings()
      qc.invalidateQueries({ queryKey: ['positions'] })
      setRoboSaveStatus(`[i] ${res?.message ?? 'Deactivated'}`)
      setTimeout(() => setRoboSaveStatus(null), 8000)
    },
    onError: (e: any) => {
      setRoboSaveStatus(`x Deactivation error: ${e.message}`)
      setTimeout(() => setRoboSaveStatus(null), 8000)
    },
  })

  const [roboForm, setRoboForm] = useState<any>(null)
  useEffect(() => {
    if (roboSettings && !roboForm) setRoboForm({ ...roboSettings })
  }, [roboSettings])

  const roboDroplet = roboStatusData?.droplet
  const roboLastScan = roboStatusData?.lastScan

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

      {/* Header */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
          <div>
            <h1 style={{ fontSize: '1.6rem', color: 'var(--bill)', letterSpacing: '-0.02em', marginBottom: 4 }}>Paper Trading</h1>
            <div className="eyebrow">Alpaca Securities · Paper Account · All trades simulated</div>
          </div>
          {account && (
            <button onClick={() => setOrderModal({})} style={{
              padding: '8px 18px', background: 'var(--cash)', color: 'var(--ink)',
              border: 'none', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800,
              fontSize: '.75rem', cursor: 'pointer',
            }}>+ New Order</button>
          )}
        </div>
        <div className="scanner-tabs" style={{ display: 'flex', border: '1px solid var(--edge)', borderRadius: 3, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {(['portfolio','orders','roboto'] as Tab[]).map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: '7px 16px', background: tab === t ? 'var(--stamp)' : 'transparent',
              border: 'none', borderBottom: tab === t ? '2px solid var(--cash)' : '2px solid transparent',
              color: tab === t ? 'var(--cash)' : 'var(--muted)',
              fontFamily: 'Syne', fontWeight: 600, fontSize: '0.72rem', cursor: 'pointer',
              whiteSpace: 'nowrap', flexShrink: 0,
            }}>{ t === 'portfolio' ? <span style={{display:'flex',alignItems:'center',gap:5}}><img src="/icons/portfolio.svg" alt="" style={{width:13,height:13,verticalAlign:'middle'}}/> Portfolio</span> : t === 'orders' ? <span style={{display:'flex',alignItems:'center',gap:5}}><img src="/icons/orders.svg" alt="" style={{width:13,height:13,verticalAlign:'middle'}}/> Orders</span> : <span style={{display:'flex',alignItems:'center',gap:5}}><img src="/icons/ai_robot.svg" alt="" style={{width:13,height:13,verticalAlign:'middle'}}/> Mr. Roboto</span> }</button>
          ))}
        </div>
      </div>

      {/* No Alpaca connected */}
      {noAlpaca && (
        <div style={{ background: 'rgba(212,168,67,.06)', border: '1px solid rgba(212,168,67,.25)', borderRadius: 4, padding: '32px 24px', textAlign: 'center', marginBottom: 16 }}>
          <img src="/icons/trading.svg" alt="" style={{width:36,height:36,opacity:.3,marginBottom:12}}/>
          <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1rem', color: 'var(--bill)', marginBottom: 8 }}>Connect your Alpaca paper account</div>
          <div style={{ fontFamily: 'Syne Mono', fontSize: '0.65rem', color: 'var(--muted)', lineHeight: 1.8, marginBottom: 20, maxWidth: 400, margin: '0 auto 20px' }}>
            Paper trading is free and requires no real money. Sign up at <strong>alpaca.markets</strong>,
            go to Paper Trading, then API Keys, then paste them in Settings.
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="https://alpaca.markets" target="_blank" rel="noopener noreferrer"
              style={{ padding: '9px 20px', background: 'transparent', color: 'var(--cash)', border: '1px solid var(--cash)', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.78rem', textDecoration: 'none' }}>
              Get Alpaca Keys 
            </a>
            <Link to="/settings" style={{ padding: '9px 22px', background: 'var(--cash)', color: 'var(--ink)', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '0.78rem', textDecoration: 'none' }}>
              Add in Settings
            </Link>
          </div>
        </div>
      )}

      {/*  Portfolio Tab  */}
      {tab === 'portfolio' && account && (
        <>
          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 16 }}>
            {[
              { label: 'Portfolio Value', val: fmt$(account.portfolio_value),              sub: null },
              { label: 'Buying Power',    val: fmt$(account.non_marginable_buying_power ?? account.cash), sub: null },
              { label: "Day P&L",         val: fmt$(dayPL),  sub: `${dayPLPct >= 0 ? '+' : ''}${dayPLPct.toFixed(2)}%`, color: clr(dayPL) },
              { label: "Total P&L",       val: fmt$(totalPL), sub: `${totalPLPct >= 0 ? '+' : ''}${totalPLPct.toFixed(2)}%`, color: clr(totalPL) },
            ].map(s => (
              <div key={s.label} style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '10px 12px', overflow: 'hidden' }}>
                <div className="eyebrow" style={{ marginBottom: 4 }}>{s.label}</div>
                <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 'clamp(0.85rem, 3vw, 1.2rem)', color: (s as any).color ?? 'var(--bill)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.val}</div>
                {s.sub && <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: (s as any).color ?? 'var(--muted)', marginTop: 2 }}>{s.sub}</div>}
              </div>
            ))}
          </div>

          {/* Positions */}
          <div className="eyebrow" style={{ marginBottom: 10 }}>Open Positions</div>
          {positions.length === 0 ? (
            <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 3, padding: '28px', textAlign: 'center', fontFamily: 'Syne Mono', fontSize: '0.65rem', color: 'var(--muted)' }}>
              No open positions  run an analysis and use "Paper Trade This" to open one
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {positions.map((p: any) => {
                const unrPL    = parseNum(p.unrealized_pl)
                const unrPLPct = parseFloat(p.unrealized_plpc ?? 0) * 100
                const isExpanded = expandedPos === p.symbol
                const related = orders.filter((o: any) => o.symbol === p.symbol && ['new','accepted','held','partially_filled'].includes(o.status))
                const sl = related.find((o: any) => o.type === 'stop' || o.order_type === 'stop')
                const tp = related.find((o: any) => (o.type === 'limit' || o.order_type === 'limit') && o.side === 'sell')
                const parentOrder = orders.find((o: any) => o.symbol === p.symbol && o.side === 'buy' && ['filled','partially_filled'].includes(o.status))
                return (
                  <div key={p.symbol} className="position-card" style={{ background: 'var(--card)', border: `1px solid ${isExpanded ? 'var(--cash-dim)' : 'var(--edge)'}`, borderRadius: 3, overflow: 'hidden', transition: 'border-color .2s' }}>
                    {/* Collapsed header — always visible */}
                    <div
                      onClick={() => setExpandedPos(isExpanded ? null : p.symbol)}
                      style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', cursor: 'pointer', userSelect: 'none' }}>
                      <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1rem', minWidth: 50, flexShrink: 0 }}>{p.symbol}</div>
                      <div style={{ flex: 1, minWidth: 100 }}>
                        <div style={{ fontFamily: 'Syne Mono', fontSize: '.68rem', marginBottom: 2 }}>
                          {parseNum(p.qty, 4)} shares · avg {fmt$(p.avg_entry_price)}
                        </div>
                        <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)' }}>
                          Current: {fmt$(p.current_price)} · Mkt Val: {fmt$(p.market_value)}
                        </div>
                      </div>
                      {/* SL/TP pills inline */}
                      {(sl || tp) && (
                        <div style={{ display: 'flex', gap: 6, fontFamily: 'Syne Mono', fontSize: '.52rem', flexShrink: 0 }}>
                          {sl && <span style={{ color: 'var(--red)', padding: '2px 6px', border: '1px solid rgba(224,92,92,.25)', borderRadius: 2 }}>SL {fmt$(sl.stop_price)}</span>}
                          {tp && <span style={{ color: 'var(--cash)', padding: '2px 6px', border: '1px solid var(--cash-dim)', borderRadius: 2 }}>TP {fmt$(tp.limit_price)}</span>}
                        </div>
                      )}
                      <div style={{ fontFamily: 'Syne Mono', fontSize: '.75rem', color: clr(unrPL), textAlign: 'right', flexShrink: 0 }}>
                        {unrPL >= 0 ? '+' : ''}{fmt$(unrPL)}
                        <div style={{ fontSize: '.58rem', opacity: .8 }}>{unrPLPct >= 0 ? '+' : ''}{unrPLPct.toFixed(2)}%</div>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); setOrderModal({ ticker: p.symbol, direction: 'SELL', qty: parseNum(p.qty, 6) }) }}
                        style={{ padding: '5px 12px', background: 'transparent', border: '1px solid var(--edge)', borderRadius: 2, color: 'var(--muted)', fontFamily: 'Syne Mono', fontSize: '.58rem', cursor: 'pointer' }}>
                        Close
                      </button>
                      <span style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)', opacity: .5, flexShrink: 0, transition: 'transform .2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)' }}>v</span>
                    </div>

                    {/* Expanded details — chart + order info */}
                    {isExpanded && (
                      <div style={{ borderTop: '1px solid var(--edge)', padding: '14px' }}>
                        {/* TradingView Mini Chart */}
                        <div style={{ position: 'relative', height: 280, marginBottom: 12, borderRadius: 3, overflow: 'hidden', border: '1px solid var(--edge)' }}>
                          <iframe
                            key={p.symbol}
                            src={`https://s.tradingview.com/widgetembed/?frameElementId=tv_chart_${p.symbol}&symbol=${p.symbol}&interval=15&hidesidetoolbar=1&symboledit=0&saveimage=0&toolbarbg=0d0d0d&studies=[]&theme=dark&style=1&timezone=America/New_York&withdateranges=0&showpopupbutton=0&allow_symbol_change=0&hide_top_toolbar=1&hide_legend=0&locale=en&colorTheme=dark&backgroundColor=rgba(13,13,13,1)&gridLineColor=rgba(40,40,40,0.6)`}
                            style={{ width: '100%', height: '100%', border: 'none' }}
                            sandbox="allow-scripts allow-same-origin allow-popups"
                            loading="lazy"
                          />
                          {/* Price level overlay — Entry / SL / TP */}
                          <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', flexDirection: 'column', gap: 4, pointerEvents: 'none', zIndex: 2 }}>
                            <div style={{ fontFamily: 'Syne Mono', fontSize: '.55rem', color: 'var(--amber)', background: 'rgba(212,168,67,.12)', border: '1px solid rgba(212,168,67,.3)', borderRadius: 2, padding: '3px 8px', backdropFilter: 'blur(4px)' }}>
                              Entry: {fmt$(p.avg_entry_price)}
                            </div>
                            {sl && (
                              <div style={{ fontFamily: 'Syne Mono', fontSize: '.55rem', color: 'var(--red)', background: 'rgba(224,92,92,.12)', border: '1px solid rgba(224,92,92,.3)', borderRadius: 2, padding: '3px 8px', backdropFilter: 'blur(4px)' }}>
                                SL: {fmt$(sl.stop_price)}
                              </div>
                            )}
                            {tp && (
                              <div style={{ fontFamily: 'Syne Mono', fontSize: '.55rem', color: 'var(--cash)', background: 'rgba(133,196,124,.12)', border: '1px solid var(--cash-dim)', borderRadius: 2, padding: '3px 8px', backdropFilter: 'blur(4px)' }}>
                                TP: {fmt$(tp.limit_price)}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Order Details Grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8, marginBottom: 10 }}>
                          <div style={{ background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '8px 10px' }}>
                            <div className="eyebrow" style={{ marginBottom: 3 }}>Side</div>
                            <div style={{ fontFamily: 'Syne Mono', fontSize: '.72rem', color: parseFloat(p.qty) >= 0 ? 'var(--cash)' : 'var(--red)', fontWeight: 700 }}>
                              {parseFloat(p.qty) >= 0 ? 'LONG' : 'SHORT'}
                            </div>
                          </div>
                          <div style={{ background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '8px 10px' }}>
                            <div className="eyebrow" style={{ marginBottom: 3 }}>Avg Entry</div>
                            <div style={{ fontFamily: 'Syne Mono', fontSize: '.72rem', color: 'var(--amber)' }}>{fmt$(p.avg_entry_price)}</div>
                          </div>
                          <div style={{ background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '8px 10px' }}>
                            <div className="eyebrow" style={{ marginBottom: 3 }}>Current</div>
                            <div style={{ fontFamily: 'Syne Mono', fontSize: '.72rem', color: 'var(--text)' }}>{fmt$(p.current_price)}</div>
                          </div>
                          <div style={{ background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '8px 10px' }}>
                            <div className="eyebrow" style={{ marginBottom: 3 }}>Qty</div>
                            <div style={{ fontFamily: 'Syne Mono', fontSize: '.72rem', color: 'var(--text)' }}>{parseNum(p.qty, 4)}</div>
                          </div>
                          <div style={{ background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '8px 10px' }}>
                            <div className="eyebrow" style={{ marginBottom: 3 }}>Market Value</div>
                            <div style={{ fontFamily: 'Syne Mono', fontSize: '.72rem', color: 'var(--text)' }}>{fmt$(p.market_value)}</div>
                          </div>
                          <div style={{ background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '8px 10px' }}>
                            <div className="eyebrow" style={{ marginBottom: 3 }}>Cost Basis</div>
                            <div style={{ fontFamily: 'Syne Mono', fontSize: '.72rem', color: 'var(--text)' }}>{fmt$(p.cost_basis)}</div>
                          </div>
                          {sl && (
                            <div style={{ background: 'rgba(224,92,92,.05)', border: '1px solid rgba(224,92,92,.2)', borderRadius: 2, padding: '8px 10px' }}>
                              <div className="eyebrow" style={{ marginBottom: 3 }}>Stop Loss</div>
                              <div style={{ fontFamily: 'Syne Mono', fontSize: '.72rem', color: 'var(--red)' }}>{fmt$(sl.stop_price)}</div>
                            </div>
                          )}
                          {tp && (
                            <div style={{ background: 'rgba(133,196,124,.05)', border: '1px solid var(--cash-dim)', borderRadius: 2, padding: '8px 10px' }}>
                              <div className="eyebrow" style={{ marginBottom: 3 }}>Take Profit</div>
                              <div style={{ fontFamily: 'Syne Mono', fontSize: '.72rem', color: 'var(--cash)' }}>{fmt$(tp.limit_price)}</div>
                            </div>
                          )}
                        </div>

                        {/* R:R ratio if SL + TP */}
                        {sl && tp && (() => {
                          const entry = parseFloat(p.avg_entry_price)
                          const slPrice = parseFloat(sl.stop_price)
                          const tpPrice = parseFloat(tp.limit_price)
                          const risk = Math.abs(entry - slPrice)
                          const reward = Math.abs(tpPrice - entry)
                          const rr = risk > 0 ? (reward / risk).toFixed(1) : '?'
                          return (
                            <div style={{ fontFamily: 'Syne Mono', fontSize: '.55rem', color: 'var(--muted)', marginBottom: 8 }}>
                              Risk/Reward: 1:{rr} · Risk ${risk.toFixed(2)} / share · Reward ${reward.toFixed(2)} / share
                            </div>
                          )
                        })()}

                        {/* Related open orders list */}
                        {related.length > 0 && (
                          <div>
                            <div className="eyebrow" style={{ marginBottom: 6 }}>Active Orders</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {related.map((o: any) => (
                                <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, fontFamily: 'Syne Mono', fontSize: '.55rem', color: 'var(--muted)', flexWrap: 'wrap' }}>
                                  <span style={{ color: o.side === 'buy' ? 'var(--cash)' : 'var(--red)', fontWeight: 700, minWidth: 30 }}>{o.side?.toUpperCase()}</span>
                                  <span>{(o.type ?? o.order_type)?.toUpperCase()}</span>
                                  <span>{o.qty} shares</span>
                                  {o.limit_price && <span>@ ${parseFloat(o.limit_price).toFixed(2)}</span>}
                                  {o.stop_price && <span>trigger ${parseFloat(o.stop_price).toFixed(2)}</span>}
                                  <span style={{ padding: '1px 5px', border: '1px solid var(--edge)', borderRadius: 2, color: 'var(--amber)', textTransform: 'uppercase' }}>{o.status?.replace(/_/g, ' ')}</span>
                                  {o.created_at && <span style={{ opacity: .5, fontSize: '.48rem', marginLeft: 'auto' }}>{new Date(o.created_at).toLocaleString()}</span>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Fill info from parent order */}
                        {parentOrder && (
                          <div style={{ fontFamily: 'Syne Mono', fontSize: '.52rem', color: 'var(--muted)', marginTop: 6, opacity: .7 }}>
                            Filled: {parentOrder.filled_avg_price ? `$${parseFloat(parentOrder.filled_avg_price).toFixed(2)}` : 'n/a'}
                            {parentOrder.filled_at ? ` at ${new Date(parentOrder.filled_at).toLocaleString()}` : ''}
                            {parentOrder.order_class ? ` · ${parentOrder.order_class}` : ''}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}

      {/*  Orders Tab  */}
      {tab === 'orders' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

          {cancelError && (
            <div style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--red)', background: 'rgba(224,92,92,.07)', border: '1px solid rgba(224,92,92,.2)', borderRadius: 2, padding: '8px 12px' }}>
              x {cancelError} <button onClick={() => setCancelError('')} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', marginLeft: 8, fontFamily: 'Syne Mono', fontSize: '.6rem' }}>dismiss</button>
            </div>
          )}

          {/* Sub-tabs */}
          <div style={{ display: 'flex', border: '1px solid var(--edge)', borderRadius: 3, overflow: 'hidden', alignSelf: 'flex-start' }}>
            {(['open', 'history'] as const).map(t => (
              <button key={t} onClick={() => setOrderTab(t)} style={{
                padding: '6px 16px', background: orderTab === t ? 'var(--stamp)' : 'transparent',
                border: 'none', borderBottom: orderTab === t ? '2px solid var(--cash)' : '2px solid transparent',
                color: orderTab === t ? 'var(--cash)' : 'var(--muted)',
                fontFamily: 'Syne', fontWeight: 600, fontSize: '0.7rem', cursor: 'pointer',
              }}>{ t === 'open' ? <span style={{display:'flex',alignItems:'center',gap:5}}><img src="/icons/trading2.svg" alt="" style={{width:13,height:13,verticalAlign:'middle'}}/> Open</span> : <span style={{display:'flex',alignItems:'center',gap:5}}><img src="/icons/history.svg" alt="" style={{width:13,height:13,verticalAlign:'middle'}}/> History</span>}</button>
            ))}
          </div>

          {/* Open orders  sourced from D1, enriched with live Alpaca status */}
          {orderTab === 'open' && (() => {
            const openStatuses = ['new','pending_new','accepted','partially_filled','held','pending_replace','pending']
            const cancellable  = ['new','pending_new','accepted','partially_filled','held','pending']
            const alpacaMap: Record<string, any> = {}
            orders.forEach((o: any) => { if (o.id) alpacaMap[o.id] = o })

            const trades = Array.isArray(myTrades) ? myTrades : []
            const openTrades = trades.filter((t: any) => {
              const liveStatus = t.alpacaOrderId ? alpacaMap[t.alpacaOrderId]?.status : null
              const status = liveStatus ?? t.status
              return openStatuses.includes(status)
            })

            return openTrades.length === 0 ? (
              <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 3, padding: '28px', textAlign: 'center', fontFamily: 'Syne Mono', fontSize: '0.65rem', color: 'var(--muted)' }}>
                No open orders
              </div>
            ) : openTrades.map((t: any) => {
              const live   = t.alpacaOrderId ? alpacaMap[t.alpacaOrderId] : null
              const status = live?.status ?? t.status
              const source = t.analysisId ? 'Analyze' : 'Scanner'
              return (
                <div key={t.id} style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 3, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.9rem', minWidth: 56, flexShrink: 0 }}>{t.ticker}</div>
                  <div style={{ flex: 1, minWidth: 100 }}>
                    <div style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', marginBottom: 1 }}>
                      <span style={{ color: t.side === 'buy' ? 'var(--cash)' : 'var(--red)', fontWeight: 700 }}>{t.side?.toUpperCase()}</span>
                      {' '}{t.qty} shares · {t.orderType}
                      {t.limitPrice ? ` @ $${t.limitPrice.toFixed(2)}` : ''}
                    </div>
                    <div style={{ fontFamily: 'Syne Mono', fontSize: '.56rem', color: 'var(--muted)', display: 'flex', gap: 8 }}>
                      <span>{new Date(t.createdAt * 1000).toLocaleString()}</span>
                      <span style={{ padding: '1px 5px', border: '1px solid var(--edge)', borderRadius: 2 }}>{source}</span>
                      {t.stopPrice && <span>SL ${t.stopPrice.toFixed(2)}</span>}
                      {t.takeProfitPrice && <span>TP ${t.takeProfitPrice.toFixed(2)}</span>}
                    </div>
                  </div>
                  <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--amber)', flexShrink: 0, textTransform: 'uppercase', padding: '2px 7px', border: '1px solid rgba(212,168,67,.3)', borderRadius: 2 }}>
                    {status.replace(/_/g, ' ')}
                  </div>
                  {cancellable.includes(status) && (() => {
                    const thisId = t.alpacaOrderId || t.id
                    const busy   = cancellingId === thisId
                    return (
                      <button
                        onClick={() => !busy && cancelMutation.mutate(thisId)}
                        disabled={busy}
                        style={{ padding: '5px 12px', background: busy ? 'rgba(224,92,92,.15)' : 'rgba(224,92,92,.08)', border: '1px solid rgba(224,92,92,.3)', borderRadius: 2, color: 'var(--red)', fontFamily: 'Syne Mono', fontSize: '.58rem', cursor: busy ? 'wait' : 'pointer', flexShrink: 0, opacity: busy ? .6 : 1 }}>
                        {busy ? 'Cancelling...' : 'Cancel Order'}
                      </button>
                    )
                  })()}
                </div>
              )
            })
          })()}

          {/* History  sourced from D1 trades (all placements) enriched with Alpaca live status */}
          {orderTab === 'history' && (() => {
            const trades = Array.isArray(myTrades) ? myTrades : []
            // Build a lookup from alpacaOrderId -> Alpaca order for live status enrichment
            const alpacaMap: Record<string, any> = {}
            orders.forEach((o: any) => { if (o.id) alpacaMap[o.id] = o })

            return trades.length === 0 ? (
              <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 3, padding: '28px', textAlign: 'center', fontFamily: 'Syne Mono', fontSize: '0.65rem', color: 'var(--muted)' }}>
                No order history yet  place your first trade from the scanner or portfolio
              </div>
            ) : trades.map((t: any) => {
              // Prefer live Alpaca status over D1 snapshot
              const live       = t.alpacaOrderId ? alpacaMap[t.alpacaOrderId] : null
              const status     = live?.status ?? t.status
              const isFilled   = status === 'filled'
              const isCanceled = status === 'canceled' || status === 'rejected' || status === 'expired'
              const isPending  = !isFilled && !isCanceled
              const statusColor = isFilled ? 'var(--cash)' : isCanceled ? 'var(--red)' : 'var(--amber)'
              const fillPrice  = live?.filled_avg_price ? parseFloat(live.filled_avg_price) : t.filledAvgPrice
              const fillQty    = live?.filled_qty ? parseFloat(live.filled_qty) : t.filledQty
              const source     = t.analysisId ? 'Analyze' : 'Scanner'
              return (
                <div key={t.id} style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 3, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', opacity: isCanceled ? .7 : 1 }}>
                  <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.9rem', minWidth: 56, flexShrink: 0 }}>{t.ticker}</div>
                  <div style={{ flex: 1, minWidth: 100 }}>
                    <div style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', marginBottom: 1 }}>
                      <span style={{ color: t.side === 'buy' ? 'var(--cash)' : 'var(--red)', fontWeight: 700 }}>{t.side?.toUpperCase()}</span>
                      {' '}{t.qty} shares · {t.orderType}
                      {t.limitPrice ? ` @ $${Number(t.limitPrice).toFixed(2)}` : ''}
                      {isFilled && fillPrice ? ` · filled $${Number(fillPrice).toFixed(2)}` : ''}
                      {isFilled && fillQty && fillQty < t.qty ? ` (${fillQty} of ${t.qty})` : ''}
                    </div>
                    <div style={{ fontFamily: 'Syne Mono', fontSize: '.56rem', color: 'var(--muted)', display: 'flex', gap: 8 }}>
                      <span>{new Date(t.createdAt * 1000).toLocaleString()}</span>
                      <span style={{ padding: '1px 5px', border: '1px solid var(--edge)', borderRadius: 2 }}>{source}</span>
                      {t.stopPrice    && <span>SL ${Number(t.stopPrice).toFixed(2)}</span>}
                      {t.takeProfitPrice && <span>TP ${Number(t.takeProfitPrice).toFixed(2)}</span>}
                    </div>
                  </div>
                  <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: statusColor, flexShrink: 0, textTransform: 'uppercase', padding: '2px 7px', border: `1px solid ${statusColor}40`, borderRadius: 2 }}>
                    {status.replace(/_/g, ' ')}
                  </div>
                  {['new','pending_new','accepted','partially_filled','held','pending'].includes(status) && (() => {
                    const thisId = t.alpacaOrderId || t.id
                    const busy   = cancellingId === thisId
                    return (
                      <button
                        onClick={() => !busy && cancelMutation.mutate(thisId)}
                        disabled={busy}
                        style={{ padding: '5px 12px', background: busy ? 'rgba(224,92,92,.15)' : 'rgba(224,92,92,.08)', border: '1px solid rgba(224,92,92,.3)', borderRadius: 2, color: 'var(--red)', fontFamily: 'Syne Mono', fontSize: '.58rem', cursor: busy ? 'wait' : 'pointer', flexShrink: 0, opacity: busy ? .6 : 1 }}>
                        {busy ? 'Cancelling...' : 'Cancel Order'}
                      </button>
                    )
                  })()}
                </div>
              )
            })
          })()}
        </div>
      )}

      {/*  Mr. Roboto Algo Tab  */}
      {tab === 'roboto' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Droplet status banner */}
          <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: roboDroplet?.status === 'connected' ? 'var(--cash)' : 'var(--red)', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.85rem', color: 'var(--bill)', marginBottom: 2 }}>
                Mr. Roboto Algo Engine
              </div>
              <div style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--muted)' }}>
                {roboDroplet
                  ? `${roboDroplet.status === 'connected' ? 'Connected' : 'Disconnected'} · ${roboDroplet.feed?.toUpperCase()} feed · ${roboDroplet.algoScans ?? 0} scans · ${roboDroplet.algoTrades ?? 0} trades today`
                  : 'Droplet status unavailable  heartbeat not received'}
              </div>
            </div>
            {roboLastScan && (
              <div style={{ fontFamily: 'Syne Mono', fontSize: '.55rem', color: 'var(--muted)', textAlign: 'right' }}>
                <div>Last scan: {new Date(roboLastScan.scannedAt).toLocaleTimeString()}</div>
                <div>{roboLastScan.surgesFound ?? 0} surges · Mode {roboLastScan.surgeMode ?? '?'}</div>
              </div>
            )}
          </div>

          {/* Last scan top surges */}
          {roboLastScan?.topSurges?.length > 0 && (
            <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '12px 16px' }}>
              <div className="eyebrow" style={{ marginBottom: 8 }}>Latest Scan Detections</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {roboLastScan.topSurges.map((s: any) => (
                  <div key={s.ticker} style={{ background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '5px 10px', fontFamily: 'Syne Mono', fontSize: '.6rem' }}>
                    <span style={{ fontFamily: 'Syne', fontWeight: 800, marginRight: 6 }}>{s.ticker}</span>
                    <span style={{ color: 'var(--cash)' }}>{s.surgeRatio}x</span>
                    <span style={{ color: 'var(--muted)', marginLeft: 6 }}>${s.price}</span>
                    <span style={{ color: (s.changePct ?? 0) >= 0 ? 'var(--cash)' : 'var(--red)', marginLeft: 6 }}>{(s.changePct ?? 0) >= 0 ? '+' : ''}{(s.changePct ?? 0).toFixed(1)}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Settings form */}
          {roboForm && (
            <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '18px' }}>
              <div className="eyebrow" style={{ marginBottom: 6 }}>Strategy Config — system-set</div>
              <div style={{ fontFamily: 'Syne Mono', fontSize: '.55rem', color: 'var(--muted)', marginBottom: 14, lineHeight: 1.6 }}>
                Mr. Roboto drives itself off live <strong>volume-surge</strong> scans — these parameters are set by the system and are <strong>not user-tunable</strong>. Your only control is on/off.
              </div>
              <div className="robo-settings-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
                {([
                  ['Surge Mode', `${roboForm.surgeMode ?? 'D'}${roboForm.surgeMode === 'D' || roboForm.surgeMode == null ? ' (session-aware)' : ''}`],
                  ['Trade Amount', `$${Number(roboForm.tradeAmount || 0).toLocaleString()}`],
                  ['Min Surge Ratio', `${roboForm.minSurgeRatio}×`],
                  ['Max Positions', String(roboForm.maxPositions)],
                  ['Stop Loss', `${roboForm.stopLossPct}%`],
                  ['Take Profit', `${roboForm.takeProfitPct}%`],
                  ['Trail Activate', `${roboForm.trailingActivatePct ?? 4}%`],
                  ['Daily Target', roboForm.dailyTargetAmount ? `$${Number(roboForm.dailyTargetAmount).toLocaleString()}` : '—'],
                ] as [string, string][]).map(([k, v]) => (
                  <div key={k}>
                    <div className="eyebrow" style={{ marginBottom: 4 }}>{k}</div>
                    <div style={{ fontFamily: 'Syne Mono', fontSize: '.78rem', color: 'var(--text)' }}>{v}</div>
                  </div>
                ))}
              </div>

              {/* Feature flags (read-only) */}
              <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                {([
                  ['topResultOnly', 'Top result only'],
                  ['forceCloseEod', 'Force close 4pm ET'],
                  ['aiFilter',      'AI Filter (Claude)'],
                  ['trailingStop',  'Trailing Stop'],
                ] as [string, string][]).map(([key, label]) => (
                  <span key={key} style={{
                    padding: '5px 12px', fontFamily: 'Syne Mono', fontSize: '.6rem', borderRadius: 2,
                    background: roboForm[key] ? 'rgba(133,196,124,.1)' : 'transparent',
                    color: roboForm[key] ? 'var(--cash)' : 'var(--muted)',
                    border: `1px solid ${roboForm[key] ? 'var(--cash-dim)' : 'var(--edge)'}`,
                  }}>{roboForm[key] ? '● ' : '○ '}{label}</span>
                ))}
              </div>

              {/* On/off — the only user control (config above is system-set) */}
              <div style={{ display: 'flex', gap: 10, marginTop: 18, alignItems: 'center', flexWrap: 'wrap' }}>
                {roboSettings?.enabled ? (
                  <button
                    onClick={() => roboDeactivateMutation.mutate()}
                    disabled={roboDeactivateMutation.isPending}
                    style={{
                      padding: '10px 20px', background: 'rgba(224,92,92,.08)', color: 'var(--red)',
                      border: '1px solid rgba(224,92,92,.3)', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800,
                      fontSize: '.78rem', cursor: 'pointer',
                    }}>
                    {roboDeactivateMutation.isPending ? 'Closing positions…' : 'Deactivate & Close All'}
                  </button>
                ) : (
                  <button
                    onClick={() => roboSaveMutation.mutate({ ...roboForm, enabled: true })}
                    disabled={roboSaveMutation.isPending}
                    style={{
                      padding: '10px 24px', background: 'var(--cash)', color: 'var(--ink)',
                      border: 'none', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800,
                      fontSize: '.78rem', cursor: 'pointer', opacity: roboSaveMutation.isPending ? .5 : 1,
                    }}>
                    {roboSaveMutation.isPending ? 'Activating…' : 'Activate Mr. Roboto'}
                  </button>
                )}
                <span style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: roboSettings?.enabled ? 'var(--cash)' : 'var(--muted)' }}>
                  {roboSettings?.enabled ? '● LIVE' : '○ Inactive'}
                </span>
              </div>

              {roboSaveStatus && (
                <div style={{
                  marginTop: 12, padding: '10px 14px', borderRadius: 3,
                  fontFamily: 'Syne Mono', fontSize: '.65rem', lineHeight: 1.6,
                  background: roboSaveStatus.startsWith('x') ? 'rgba(224,92,92,.08)' : roboSaveStatus.startsWith('[i]') ? 'rgba(212,168,67,.08)' : 'rgba(133,196,124,.08)',
                  border: `1px solid ${roboSaveStatus.startsWith('x') ? 'rgba(224,92,92,.25)' : roboSaveStatus.startsWith('[i]') ? 'rgba(212,168,67,.25)' : 'rgba(133,196,124,.25)'}`,
                  color: roboSaveStatus.startsWith('x') ? 'var(--red)' : roboSaveStatus.startsWith('[i]') ? 'var(--amber)' : 'var(--cash)',
                }}>
                  {roboSaveStatus}
                </div>
              )}
            </div>
          )}

          {/* How it works */}
          <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '14px 18px' }}>
            <div className="eyebrow" style={{ marginBottom: 8 }}>How Mr. Roboto Algo Works</div>
            <div style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--muted)', lineHeight: 1.9 }}>
              When activated, Mr. Roboto runs a full volume surge scan every 60 seconds using real-time Polygon + Alpaca SIP data.
              Stocks with price $0.10$50 showing abnormal volume surges and positive price momentum are detected. Qualifying surges
              trigger automatic bracket orders (market buy + stop loss + take profit) on your Alpaca paper account. The droplet also
              monitors the Alpaca WebSocket SIP feed for supplementary real-time surge detection between scan cycles.
              All positions are auto-closed at 3:50pm ET when Force Close EOD is enabled.
              <span style={{ color: 'var(--cash)', fontWeight: 700 }}> Mode D (session-aware) is recommended</span>  it adapts
              its baseline algorithm to premarket, early open, and regular session conditions automatically.
            </div>
            {roboForm?.aiFilter && (
              <div style={{ marginTop: 10, padding: '10px 14px', background: 'rgba(133,196,124,.04)', border: '1px solid rgba(133,196,124,.15)', borderRadius: 3, fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--muted)', lineHeight: 1.9 }}>
                <span style={{ color: 'var(--cash)', fontWeight: 700 }}>AI Filter enabled:</span> Before placing any trade, Claude AI evaluates each
                surge candidate with recent news headlines and 1-hour price action. Claude decides whether to trade or skip (filtering out offerings,
                dilution, exhausted runners, pump-and-dumps) and sets an <span style={{ color: 'var(--cash)' }}>optimal take-profit price</span> based
                on nearby resistance levels, float dynamics, and volume momentum  instead of using a fixed %. This adds ~2-3s latency per scan cycle
                but significantly improves trade quality and expected returns.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <div style={{ marginTop: 24, background: 'rgba(212,168,67,.04)', border: '1px solid rgba(212,168,67,.15)', borderRadius: 3, padding: '12px 14px', fontFamily: 'Syne Mono', fontSize: '.56rem', color: 'var(--muted)', lineHeight: 1.8 }}>
        Paper trading only. No real money is involved. All trades are simulated using Alpaca's paper trading environment.
        Polywally is not a registered broker-dealer or investment advisor. AI analysis is for educational purposes only.{' '}
        <Link to="/terms" style={{ color: 'var(--cash)', textDecoration: 'none' }}>Terms</Link>{'  ·  '}<Link to="/disclaimer" style={{ color: 'var(--cash)', textDecoration: 'none' }}>Disclaimer</Link>
      </div>

      {orderModal !== null && (
        <OrderModal prefill={Object.keys(orderModal).length ? orderModal : undefined} onClose={() => setOrderModal(null)} />
      )}
    </div>
  )
}

